import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Checkbox, FormControlLabel, CircularProgress } from '@mui/material';
import { CardButton } from '../../Compounds/Buttons/CardButton';
import { PageLayout } from '../../PageLayout';
import { PageCard } from '../../Compounds/CardWithTitle';
import {
  fetchProjectById,
  fetchProjects,
  getProjectById,
  getProjectLoadingSubject,
  getProjects,
  getProjectSubject,
  Project
} from '../../../store/Project';
import { getErrorByDomainSubject } from '../../../store/Error';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { ProjectDetailsSection } from './ProjectDetailsSection';
import { AttachmentTable } from '../../Compounds/AttachmentTable';
import { useAttachments } from '../../../store/Attachment';
import { ProjectsRelatedInventory } from './ProjectsRelatedInventory';
import { ProjectSingleAssetView } from './ProjectSingleAssetView';
import { riskCountTotal, RiskSummaryWidget } from '../../Compounds/RiskSummaryWidget';
import { StatusUpdateSection } from '../../Compounds/StatusUpdate';
import { DrawerActions } from '../../Compounds/DetailsDrawer/DrawerActions';
import { RecordUnavailable } from '../../Compounds/RecordUnavailable/RecordUnavailable';
import {
  getSelectedAccountIdsForUrl,
  getSelectedAccountsSubject,
  getUser,
  isInternalStaff,
  isProjectsUpdatesEnabled
} from '../../../store/User';
import { updateProjectPortalOptIn } from '../../../Api/Projects/projectsApi';
import { useFeatureFlags } from '../../../Api/useFeatureFlags';
import { useSnackbar } from '../../../Context/SnackbarContext';

export const ProjectsDetailPage = () => {
  const [project, setProject] = useState<Project>();
  // Held so the first paint cannot flash the unavailable notice before the store reports its state.
  const [projectLoading, setProjectLoading] = useState<boolean>(true);
  const [optInLoading, setOptInLoading] = useState<boolean>(false);
  const [riskLoading, setRiskLoading] = useState<boolean>(false);
  const [accountScope, setAccountScope] = useState<string[]>(getSelectedAccountIdsForUrl());
  const navigate = useNavigate();
  const { id } = useParams();
  const [search] = useSearchParams();
  // Set when the row came from the Projects page's Inventory mode, which addresses one asset of a
  // project however many it holds — so the 1:1 view here is not the singleAsset case's alone.
  const assetId = search.get('assetId');
  const { setSnack } = useSnackbar();
  const { config: featureFlags } = useFeatureFlags();
  const riskFetchedId = useRef<string | undefined>(undefined);
  useAttachments();

  const user = getUser();
  const showOptIn = isInternalStaff(user);
  // An empty list plus a project-domain error is a failed load, not a missing project.
  const listLoadFailed = !getProjects().length && !!getErrorByDomainSubject().value.project;
  // The account scope lands after the first render, and the list cannot be asked for without it.
  const scopePending = !accountScope.length && !!user?.accounts?.length;
  const projectsUpdatesEnabled = isProjectsUpdatesEnabled(featureFlags);
  const riskCounts = project?.riskCounts;
  const widgetRiskCounts =
    projectsUpdatesEnabled && riskCounts && riskCountTotal(riskCounts) > 0 ? riskCounts : undefined;
  // Inventory mode is offered on every environment, so an addressed asset needs the detail payload
  // here whether or not the flag driving the risk widget is on.
  const detailNeeded = projectsUpdatesEnabled || !!assetId;
  const detailDataMissing = detailNeeded && !!project && !riskCounts;
  const detailPending = detailDataMissing && (riskLoading || riskFetchedId.current !== id);
  const addressedItem =
    !detailPending && assetId ? project?.relatedInventory?.find((item) => item.id === assetId) : undefined;
  // Unlike an addressed asset, the whole-project 1:1 layout stays behind the flag — turning it on
  // for flag-off environments would change how an ordinary single-asset project opens.
  const singleAssetItem =
    projectsUpdatesEnabled && !detailPending && project?.singleAsset ? project.relatedInventory?.[0] : undefined;
  const viewedItem = addressedItem ?? singleAssetItem;
  const singleAssetView = project && viewedItem ? { project, item: viewedItem } : null;

  const toggleBackClicked = () => {
    // A deep-linked tab has no in-app history to go back to; fall back to the Projects page.
    if (window.history.state?.idx > 0) {
      navigate(-1);
    } else {
      navigate('/projects');
    }
  };

  const handleOptInToggle = async (enabled: boolean) => {
    if (!id) return;
    setOptInLoading(true);
    const { error } = await updateProjectPortalOptIn(id, enabled);
    setOptInLoading(false);
    if (error) {
      setSnack({ message: `Failed to update project portal status: ${error.message}`, type: 'error', open: true });
    } else {
      setProject((prev) => (prev ? { ...prev, managedInClientPortal: enabled } : prev));
      setSnack({
        message: `Project portal tracking ${enabled ? 'enabled' : 'disabled'} successfully`,
        type: 'success',
        open: true
      });
    }
  };

  useEffect(() => {
    const projectLoadingSub = getProjectLoadingSubject().subscribe((loading) => setProjectLoading(loading));
    const projectsSubject = getProjectSubject().subscribe(() => setProject(getProjectById(id || '')));
    // A project opened before the account scope arrived would otherwise never be asked for.
    const accountsSub = getSelectedAccountsSubject().subscribe(() => {
      setAccountScope(getSelectedAccountIdsForUrl());
      // Non-force: this only needs to win the empty-store cold load; account switches force-refresh elsewhere.
      fetchProjects();
    });
    fetchProjects();

    return () => {
      if (projectLoadingSub) projectLoadingSub.unsubscribe();
      if (projectsSubject) projectsSubject.unsubscribe();
      if (accountsSub) accountsSub.unsubscribe();
    };
  }, [id]);

  useEffect(() => {
    if (!projectLoading && id) {
      setProject(getProjectById(id));
    }
  }, [id, projectLoading]);

  useEffect(() => {
    // Once risk data is present, clear the guard so a later list refresh (which drops
    // riskCounts from the project) re-enriches instead of leaving the widget blank.
    if (project?.riskCounts) {
      riskFetchedId.current = undefined;
      return;
    }
    const needsDetailFetch = detailNeeded && id && project && !projectLoading && riskFetchedId.current !== id;
    if (needsDetailFetch) {
      riskFetchedId.current = id;
      setRiskLoading(true);
      fetchProjectById(id, true).finally(() => setRiskLoading(false));
    }
  }, [detailNeeded, id, project, projectLoading]);

  // An addressed asset can be missing from the payload: these rows are reached by URL, so a link
  // outlives the record it names, and a scope change can hide one that is still on the project.
  // The project is still worth showing, but say so — otherwise the fallback renders a plausible
  // page for a record the user never asked for. Guarded per project/asset pair so it says it once.
  const missingAssetNoticeKey = useRef<string | undefined>(undefined);
  useEffect(() => {
    // relatedInventory absent means the detail payload itself never arrived, which is a different
    // failure from a record that is not there — claiming the latter would be a guess.
    if (!assetId || detailPending || !project?.relatedInventory || addressedItem) return;
    const noticeKey = `${id}:${assetId}`;
    if (missingAssetNoticeKey.current === noticeKey) return;

    missingAssetNoticeKey.current = noticeKey;
    setSnack({
      message: 'That inventory record is not available on this project. Showing the project instead.',
      type: 'warning',
      open: true
    });
  }, [assetId, id, detailPending, project?.relatedInventory, addressedItem]);

  const pageActions = () => {
    return (
      <div className="flex items-center gap-x-2">
        {showOptIn && project && (
          <div className="flex items-center gap-x-1">
            {optInLoading && <CircularProgress size={16} />}
            <FormControlLabel
              control={
                <Checkbox
                  checked={project.managedInClientPortal ?? false}
                  disabled={optInLoading}
                  onChange={(e) => handleOptInToggle(e.target.checked)}
                  size="small"
                  inputProps={{ 'aria-label': 'Project Tracked via Portal' }}
                />
              }
              label="Project Tracked via Portal"
              sx={{ marginRight: 0 }}
            />
          </div>
        )}
        {project && (
          <DrawerActions
            componentName="project"
            data={project}
            setShow={() => {}}
          />
        )}
        <CardButton
          text="Back"
          onClick={toggleBackClicked}
          className="h-8 px-2 pl-4 ml-auto border border-grey-500 rounded-lg shadow"
        />
      </div>
    );
  };

  if (!projectLoading && !scopePending && !project) {
    // The notice carries the only way out, so the header's history-based Back is left off here.
    return (
      <PageLayout pageTitle="Project Details">
        <RecordUnavailable
          message={
            listLoadFailed
              ? 'The projects list could not be loaded, so this project cannot be shown.'
              : 'This project is not available for this account. It may have been hidden or removed.'
          }
          actionText="Back to Projects"
          onAction={() => navigate('/projects')}
          dataTestId="project-unavailable"
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle="Project Details"
      pageActions={pageActions()}>
      {detailPending ? (
        // Hold the layout until the detail response resolves the addressed asset (or singleAsset)
        // — otherwise the 1:many layout would flash before switching to the full-screen 1:1 view.
        <div
          className="mt-16"
          data-testid="project-detail-pending">
          <ComponentSpinner />
        </div>
      ) : singleAssetView ? (
        <ProjectSingleAssetView
          project={singleAssetView.project}
          item={singleAssetView.item}
          tagEntity={addressedItem ? 'asset' : 'project'}
        />
      ) : (
        <>
          {/* Summary + Details share the single-asset page's layout: equal-height siblings (items-stretch),
              with the Details content scrolling — so the card matches the summary height on both pages. */}
          <div className="flex items-stretch gap-4 mt-6">
            {widgetRiskCounts && (
              <RiskSummaryWidget
                className="w-1/2"
                riskCounts={widgetRiskCounts}
                relatedInventory={project?.relatedInventory}>
                <StatusUpdateSection entityId={id} />
              </RiskSummaryWidget>
            )}
            <PageCard
              title="Details"
              className={`${widgetRiskCounts ? 'w-1/2' : 'w-full'} !my-0`}>
              <div className="overflow-auto max-h-[44vh]">
                {!project && (
                  <div className="mt-16">
                    <ComponentSpinner />
                  </div>
                )}
                {project && <ProjectDetailsSection project={project} />}
              </div>
            </PageCard>
          </div>
          <div className="mt-4">
            <AttachmentTable
              id={id || ''}
              className="max-h-[27vh] !overflow-y-hidden"
              childrenWrapperClass="max-h-[22vh] !overflow-y-scroll"
              titlePadding="p-5"
              parentType="Project__c"
            />
          </div>
          {/* Mount only once the project resolves so the table starts in its final mode
              (risk rows vs. inventory store) instead of flipping after the first render. */}
          {project && (
            <ProjectsRelatedInventory
              id={id || ''}
              relatedInventory={projectsUpdatesEnabled ? project.relatedInventory : undefined}
              accountId={project.account?.id}
            />
          )}
        </>
      )}
    </PageLayout>
  );
};
